<template>
  <div class="form-section mb-2">
    <h3 class="section-title mb-1">
      <v-icon color="primary" class="mr-2">mdi-image-multiple</v-icon>
      Product Images
      <v-spacer></v-spacer>
      <v-chip
        x-small
        :color="images.length > 0 ? 'success' : 'info'"
        outlined
        class="ml-2"
      >
        {{ images.length }} {{ images.length === 1 ? "Image" : "Images" }}
      </v-chip>
      <!-- Debug button for testing modal -->
      <v-btn
        x-small
        color="warning"
        text
        @click="testModal"
        class="ml-2"
        v-if="images.length > 0"
      >
        Test Modal
      </v-btn>
      <!-- Debug state indicator -->
      <v-chip
        x-small
        :color="showImagePreview ? 'success' : 'error'"
        class="ml-1"
        v-if="images.length > 0"
      >
        Modal: {{ showImagePreview ? "Open" : "Closed" }}
      </v-chip>
      <!-- Manual refresh button -->
      <v-btn
        x-small
        color="info"
        text
        @click="loadImages"
        class="ml-2"
        v-if="productId"
      >
        <v-icon x-small>mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </h3>

    <!-- Image Upload Section (Only show if not readonly) -->
    <div v-if="!readonly" class="image-upload-section mb-4">
      <v-card outlined class="upload-card">
        <v-card-text class="pa-3">
          <div
            class="upload-area"
            @dragover.prevent
            @dragenter.prevent
            @drop.prevent="handleFileDrop"
            :class="{ 'drag-over': isDragOver }"
            @dragenter="isDragOver = true"
            @dragleave="isDragOver = false"
          >
            <input
              ref="fileInput"
              type="file"
              multiple
              accept="image/*"
              style="display: none"
              @change="handleFileSelect"
            />

            <div class="upload-content text-center">
              <v-icon size="48" color="primary" class="mb-3"
                >mdi-cloud-upload</v-icon
              >
              <h4 class="upload-title mb-2">Upload Product Images</h4>
              <p class="upload-subtitle mb-3 grey--text">
                Drag and drop images here, or click to browse
              </p>
              <v-btn
                color="primary"
                outlined
                @click="$refs.fileInput.click()"
                :loading="uploading"
                :disabled="uploading"
                class="upload-button"
              >
                <v-icon left>mdi-file-image</v-icon>
                Choose Images
              </v-btn>
              <div class="upload-info mt-3">
                <v-chip x-small color="info" outlined class="mr-1">
                  <v-icon left x-small>mdi-information</v-icon>
                  Max 10 images
                </v-chip>
                <v-chip x-small color="warning" outlined>
                  <v-icon left x-small>mdi-file-image</v-icon>
                  JPG, PNG, GIF up to 5MB each
                </v-chip>
              </div>
            </div>
          </div>

          <!-- Upload Progress -->
          <div v-if="uploadProgress.length > 0" class="upload-progress mt-3">
            <h4 class="text-subtitle-2 mb-2">Uploading Images...</h4>
            <div
              v-for="(progress, index) in uploadProgress"
              :key="index"
              class="progress-item mb-2"
            >
              <div class="d-flex align-center">
                <v-icon small color="info" class="mr-2">mdi-file-image</v-icon>
                <span class="text-body-2 flex-grow-1">{{ progress.name }}</span>
                <span class="text-caption"
                  >{{ Math.round(progress.progress) }}%</span
                >
              </div>
              <v-progress-linear
                :value="progress.progress"
                color="primary"
                height="4"
                class="mt-1"
              ></v-progress-linear>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Images Gallery -->
    <div class="images-gallery">
      <v-row v-if="images.length === 0" class="no-images">
        <v-col cols="12">
          <v-card outlined class="empty-state">
            <v-card-text class="text-center py-8">
              <v-icon size="80" color="grey lighten-1" class="mb-3"
                >mdi-image-off</v-icon
              >
              <h3 class="grey--text mb-2">No images uploaded</h3>
              <p class="grey--text text--lighten-1 mb-0">
                {{
                  readonly
                    ? "This product has no images."
                    : "Upload images to showcase your product."
                }}
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <div v-else>
        <div class="images-grid">
          <v-row>
            <v-col
              v-for="(image, index) in images"
              :key="image.id"
              cols="12"
              sm="6"
              md="4"
              lg="3"
              class="image-item"
            >
              <v-card
                class="image-card"
                :class="{ 'primary-image': index === 0 }"
                elevation="2"
              >
                <!-- Image Badge for Primary -->
                <div v-if="index === 0" class="primary-badge">
                  <v-chip x-small color="success" text-color="white">
                    <v-icon left x-small>mdi-star</v-icon>
                    Primary
                  </v-chip>
                </div>

                <!-- Sort Order Badge -->
                <div class="sort-badge">
                  <v-chip x-small color="info" text-color="white">
                    {{ image.sort_order || index + 1 }}
                  </v-chip>
                </div>

                <!-- Image Container -->
                <div
                  class="image-container"
                  @click="openImagePreview(image, index)"
                >
                  <v-img
                    :src="getImageUrl(image.image_url)"
                    :alt="'Product image ' + (index + 1)"
                    aspect-ratio="1"
                    class="image-preview"
                    @click.stop="openImagePreview(image, index)"
                  >
                    <template v-slot:placeholder>
                      <v-row
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                      >
                        <v-progress-circular
                          indeterminate
                          color="primary"
                        ></v-progress-circular>
                      </v-row>
                    </template>
                    <template v-slot:error>
                      <v-row
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                      >
                        <div class="text-center">
                          <v-icon size="48" color="grey"
                            >mdi-image-broken</v-icon
                          >
                          <div class="text-caption grey--text mt-2">
                            Failed to load
                          </div>
                          <div
                            class="text-caption grey--text"
                            style="font-size: 10px"
                          >
                            {{ getImageUrl(image.image_url) }}
                          </div>
                        </div>
                      </v-row>
                    </template>
                  </v-img>

                  <!-- Image Overlay on Hover -->
                  <div class="image-overlay d-flex align-center justify-center">
                    <v-btn
                      icon
                      large
                      color="white"
                      @click.stop="openImagePreview(image, index)"
                    >
                      <v-icon>mdi-magnify</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Image Actions -->
                <v-card-actions v-if="!readonly" class="pa-2">
                  <v-btn
                    icon
                    small
                    color="info"
                    @click="moveImageUp(index)"
                    :disabled="index === 0"
                    title="Move Up"
                  >
                    <v-icon small>mdi-arrow-up</v-icon>
                  </v-btn>

                  <v-btn
                    icon
                    small
                    color="info"
                    @click="moveImageDown(index)"
                    :disabled="index === images.length - 1"
                    title="Move Down"
                  >
                    <v-icon small>mdi-arrow-down</v-icon>
                  </v-btn>

                  <v-spacer></v-spacer>

                  <v-btn
                    icon
                    small
                    color="error"
                    @click="confirmDeleteImage(image, index)"
                    title="Delete Image"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-card-actions>

                <!-- Read-only badge for readonly mode -->
                <v-card-actions v-else class="pa-2">
                  <v-chip x-small color="grey" outlined>
                    <v-icon left x-small>mdi-eye</v-icon>
                    View Only
                  </v-chip>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>

    <!-- Image Preview Dialog -->
    <v-dialog
      v-model="showImagePreview"
      max-width="800px"
      :value="showImagePreview"
      persistent
      :z-index="1000"
      eager
    >
      <v-card>
        <v-card-title class="d-flex align-center">
          <span class="text-h6">Image Preview</span>
          <v-spacer></v-spacer>
          <v-chip small color="primary" outlined>
            {{ currentImageIndex + 1 }} of {{ images.length }}
          </v-chip>
          <v-btn icon @click="closeImagePreview">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <div class="image-preview-container">
            <v-img
              v-if="currentPreviewImage"
              :src="getImageUrl(currentPreviewImage.image_url)"
              :alt="'Product image ' + (currentImageIndex + 1)"
              max-height="500"
              contain
            ></v-img>
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn
            text
            color="primary"
            @click="previousImage"
            :disabled="currentImageIndex === 0"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Previous
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
            text
            color="primary"
            @click="nextImage"
            :disabled="currentImageIndex === images.length - 1"
          >
            Next
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline error white--text d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-delete-alert</v-icon>
          Delete Image
        </v-card-title>

        <v-card-text class="pt-4">
          <div class="text-center">
            <v-img
              v-if="imageToDelete"
              :src="getImageUrl(imageToDelete.image_url)"
              max-height="200"
              max-width="200"
              class="mx-auto mb-3 rounded"
            ></v-img>
            <h3 class="mb-3">Are you sure you want to delete this image?</h3>
            <p class="text-body-2 grey--text mb-1">
              <strong>Warning:</strong> This action cannot be undone.
            </p>
            <p class="text-body-2 grey--text">
              The image will be permanently removed from the product.
            </p>
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="deleteDialog = false"
            :disabled="deleting"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            @click="deleteImage"
            :loading="deleting"
            :disabled="deleting"
          >
            <v-icon left>mdi-delete</v-icon>
            Delete Image
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "ProductImageUpload",
  props: {
    productId: {
      type: [String, Number],
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      images: [],
      uploading: false,
      isDragOver: false,
      uploadProgress: [],
      showImagePreview: false,
      currentPreviewImage: null,
      currentImageIndex: 0,
      deleteDialog: false,
      imageToDelete: null,
      imageToDeleteIndex: null,
      deleting: false,
      maxFiles: 10,
      maxFileSize: 5 * 1024 * 1024, // 5MB
      allowedTypes: ["image/jpeg", "image/png", "image/gif", "image/webp"],
      mediaURL: "https://blr1.vultrobjects.com/space-1/",
    };
  },
  mounted() {
    if (this.productId) {
      this.loadImages();
    }
  },
  watch: {
    productId(newId) {
      if (newId) {
        this.loadImages();
      } else {
        this.images = [];
      }
    },
  },
  methods: {
    async loadImages() {
      if (!this.productId) {
        console.log("No productId provided, skipping image load");
        return;
      }

      console.log("Loading images for product ID:", this.productId);

      try {
        const response = await axios.get(
          `/products/${this.encryptIt(this.productId)}/images`
        );
        console.log("API response:", response);

        if (response.data.success) {
          const rawImages = response.data.data || [];
          console.log("Raw images from API:", rawImages);

          this.images = rawImages.sort(
            (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
          );
          console.log("Sorted images:", this.images);
          console.log("Total images loaded:", this.images.length);

          // Test URL generation for first image
          if (this.images.length > 0) {
            const testUrl = this.getImageUrl(this.images[0].image_url);
            console.log("Test image URL:", testUrl);
          }

          this.$emit("images-loaded", this.images);
        } else {
          console.log("API returned success: false", response.data);
          this.images = [];
        }
      } catch (error) {
        console.error("Error loading images:", error);
        console.error("Error details:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });
        this.$store.dispatch("errors/setError", {
          message: "Failed to load product images.",
        });
        this.images = [];
      }
    },

    handleFileDrop(event) {
      this.isDragOver = false;
      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.processFiles(files);
      // Clear the input so the same file can be selected again
      event.target.value = "";
    },

    processFiles(files) {
      if (!this.productId) {
        this.$store.dispatch("errors/setError", {
          message: "Please save the product first before uploading images.",
        });
        return;
      }

      // Validate number of files
      if (this.images.length + files.length > this.maxFiles) {
        this.$store.dispatch("errors/setError", {
          message: `Maximum ${this.maxFiles} images allowed. Currently you have ${this.images.length} images.`,
        });
        return;
      }

      // Filter and validate files
      const validFiles = files.filter((file) => {
        // Check file type
        if (!this.allowedTypes.includes(file.type)) {
          this.$store.dispatch("errors/setError", {
            message: `Invalid file type: ${file.name}. Only JPG, PNG, GIF, and WebP are allowed.`,
          });
          return false;
        }

        // Check file size
        if (file.size > this.maxFileSize) {
          this.$store.dispatch("errors/setError", {
            message: `File too large: ${file.name}. Maximum size is 5MB.`,
          });
          return false;
        }

        return true;
      });

      if (validFiles.length === 0) {
        return;
      }

      this.uploadFiles(validFiles);
    },

    async uploadFiles(files) {
      this.uploading = true;
      this.uploadProgress = [];

      try {
        const formData = new FormData();
        formData.append("product_id", this.productId);

        files.forEach((file) => {
          formData.append("images[]", file);
          this.uploadProgress.push({
            name: file.name,
            progress: 0,
          });
        });

        const response = await axios.post("/products/images/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            // Update all progress bars with the same progress for simplicity
            this.uploadProgress.forEach((item) => {
              item.progress = progress;
            });
          },
        });

        if (response.data.success) {
          // Add new images to the list
          if (response.data.data && response.data.data.length > 0) {
            this.images.push(...response.data.data);
            this.images.sort(
              (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
            );

            this.$store.dispatch("errors/setError", {
              message: `${response.data.data.length} image(s) uploaded successfully!`,
              type: "success",
            });

            this.$emit("images-uploaded", response.data.data);
          }

          // Show any upload errors
          if (response.data.errors && response.data.errors.length > 0) {
            response.data.errors.forEach((error) => {
              this.$store.dispatch("errors/setError", {
                message: error,
                type: "warning",
              });
            });
          }
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to upload images.",
          });
        }
      } catch (error) {
        console.error("Upload error:", error);
        if (error.response && error.response.status === 422) {
          // Handle validation errors
          const errors = error.response.data.errors || {};
          Object.keys(errors).forEach((key) => {
            errors[key].forEach((message) => {
              this.$store.dispatch("errors/setError", { message });
            });
          });
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Failed to upload images. Please try again.",
          });
        }
      } finally {
        this.uploading = false;
        this.uploadProgress = [];
      }
    },

    getImageUrl(imagePath) {
      if (!imagePath) return "";

      // If it's already a full URL, return as is
      if (imagePath.startsWith("http")) {
        return imagePath;
      }

      // Try to get media URL from store first
      try {
        const mediaUrl = this.$store.getters["settings/getMediaUrl"];
        console.log("Media Url ====>", mediaUrl);
        console.log("Image======>", `${mediaUrl}${imagePath}`);
        if (mediaUrl) {
          return `${mediaUrl}${imagePath}`;
        }
      } catch (error) {
        console.log("Store mediaUrl not available, using fallback");
      }

      // Fallback to environment variable or default
      // const baseUrl =
      //   process.env.VUE_APP_API_URL?.replace("/api", "") ||
      //   "http://localhost:8001";
      return `${this.mediaURL}${imagePath}`;
    },

    openImagePreview(image, index) {
      console.log("Opening image preview:", image, index);
      console.log("Current showImagePreview state:", this.showImagePreview);
      console.log("Setting currentPreviewImage to:", image);
      console.log("Setting currentImageIndex to:", index);

      this.currentPreviewImage = image;
      this.currentImageIndex = index;
      this.showImagePreview = true;

      console.log("After setting - showImagePreview:", this.showImagePreview);
      console.log(
        "After setting - currentPreviewImage:",
        this.currentPreviewImage
      );
    },

    closeImagePreview() {
      console.log("Closing image preview");
      this.showImagePreview = false;
      this.currentPreviewImage = null;
      this.currentImageIndex = 0;
    },

    testModal() {
      console.log("Test modal clicked - current state:", this.showImagePreview);
      console.log("Images available:", this.images);
      if (this.images && this.images.length > 0) {
        this.openImagePreview(this.images[0], 0);
      } else {
        console.log("No images available for preview");
      }
    },

    previousImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
        this.currentPreviewImage = this.images[this.currentImageIndex];
      }
    },

    nextImage() {
      if (this.currentImageIndex < this.images.length - 1) {
        this.currentImageIndex++;
        this.currentPreviewImage = this.images[this.currentImageIndex];
      }
    },

    confirmDeleteImage(image, index) {
      this.imageToDelete = image;
      this.imageToDeleteIndex = index;
      this.deleteDialog = true;
    },

    async deleteImage() {
      if (!this.imageToDelete) return;

      this.deleting = true;

      try {
        const response = await axios.delete(
          `/products/images/${this.imageToDelete.id}`
        );

        if (response.data.success) {
          // Remove image from the list
          this.images.splice(this.imageToDeleteIndex, 1);

          this.$store.dispatch("errors/setError", {
            message: "Image deleted successfully!",
            type: "success",
          });

          this.$emit("image-deleted", this.imageToDelete);
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to delete image.",
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        this.$store.dispatch("errors/setError", {
          message: "Failed to delete image. Please try again.",
        });
      } finally {
        this.deleting = false;
        this.deleteDialog = false;
        this.imageToDelete = null;
        this.imageToDeleteIndex = null;
      }
    },

    async moveImageUp(index) {
      if (index === 0) return;

      await this.swapImages(index, index - 1);
    },

    async moveImageDown(index) {
      if (index === this.images.length - 1) return;

      await this.swapImages(index, index + 1);
    },

    async swapImages(index1, index2) {
      try {
        // Update sort orders
        const image1 = this.images[index1];
        const image2 = this.images[index2];

        const reorderData = {
          product_id: this.productId,
          image_orders: [
            { id: image1.id, sort_order: index2 + 1 },
            { id: image2.id, sort_order: index1 + 1 },
          ],
        };

        const response = await axios.patch(
          "/products/images/reorder",
          reorderData
        );

        if (response.data.success) {
          // Update local state
          [this.images[index1], this.images[index2]] = [
            this.images[index2],
            this.images[index1],
          ];

          // Update sort orders
          this.images[index1].sort_order = index1 + 1;
          this.images[index2].sort_order = index2 + 1;

          this.$toast.success("Image order updated successfully!");
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to reorder images.",
          });
        }
      } catch (error) {
        console.error("Reorder error:", error);
        this.$store.dispatch("errors/setError", {
          message: "Failed to reorder images. Please try again.",
        });
      }
    },
  },
};
</script>

<style scoped>
/* Section Styling */
.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Upload Section */
.upload-card {
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.upload-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9ff;
}

.upload-area {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.upload-area.drag-over {
  background-color: #e3f2fd;
  border-color: var(--v-primary-base);
  transform: scale(1.02);
}

.upload-content {
  padding: 2rem;
}

.upload-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--v-primary-base);
}

.upload-subtitle {
  font-size: 0.9rem;
  line-height: 1.4;
}

.upload-button {
  text-transform: none;
  font-weight: 500;
}

.upload-info {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Progress Section */
.progress-item {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 6px;
  border-left: 3px solid var(--v-primary-base);
}

/* Images Gallery */
.images-gallery {
  min-height: 100px;
}

.empty-state {
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.images-grid {
  margin-top: 1rem;
}

.image-item {
  position: relative;
}

.image-card {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.image-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
}

.image-card.primary-image {
  border: 2px solid var(--v-success-base);
}

.primary-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
}

.sort-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.image-container {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  user-select: none;
}

.image-preview {
  border-radius: 0;
  transition: all 0.3s ease;
}

.image-container:hover .image-preview {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  opacity: 0;
  transition: all 0.3s ease;
  z-index: 2;
}

.image-container:hover .image-overlay {
  opacity: 1;
}

/* Dialog Styling */
.image-preview-container {
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  position: relative;
}

/* Responsive Design */
@media (max-width: 600px) {
  .upload-content {
    padding: 1rem;
  }

  .upload-title {
    font-size: 1rem;
  }

  .upload-subtitle {
    font-size: 0.8rem;
  }

  .upload-info {
    flex-direction: column;
    align-items: center;
  }

  .image-card {
    margin-bottom: 1rem;
  }
}

/* Animation Classes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.3s;
}

.list-enter,
.list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

/* Custom Scrollbar */
.upload-progress::-webkit-scrollbar {
  width: 4px;
}

.upload-progress::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.upload-progress::-webkit-scrollbar-thumb {
  background: var(--v-primary-base);
  border-radius: 2px;
}

.upload-progress::-webkit-scrollbar-thumb:hover {
  background: var(--v-primary-darken1);
}

/* Utility Classes */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Professional Styling */
.v-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-btn {
  transition: all 0.2s ease;
}

.v-chip {
  font-weight: 500;
}

/* Focus States */
.upload-area:focus-within {
  outline: 2px solid var(--v-primary-base);
  outline-offset: 2px;
}

/* Loading States */
.image-card.loading {
  opacity: 0.7;
  pointer-events: none;
}

/* Error States */
.upload-card.error {
  border-color: var(--v-error-base);
  background-color: #ffebee;
}

/* Success States */
.upload-card.success {
  border-color: var(--v-success-base);
  background-color: #e8f5e8;
}
</style>